/**
 * C-GTCC-VIEW-DIAG-AIA1 — typed read client for the "AI & Analytics" view.
 *
 * All four paths already exist and are reused, not re-invented:
 *   `/v1/admin/ai/provider-registry`  (ai-workbench, allowlist of providers)
 *   `/v1/insights/analytics`          (question analytics, aggregate counts)
 *   `/v1/admin/ai/gi-coverage-gaps`   (GI coverage gaps, redacted groups)
 *   `/v1/admin/ai/usage`              (AI usage per tenant, cost unverified)
 *
 * None of them carries the GTCC `status`/`freshnessSeconds` convention, so
 * the view prints "Alter nicht messbar" instead of deriving an age from the
 * local clock (Owner decisions P8/P9/P10). Cost is shown exactly as the
 * payload declares it: `costVerified === false` reads "unverified" and never
 * as a currency amount.
 *
 * Read-only by construction: `gtccGet` wraps `apiGet` only. The one action
 * the view offers — the workbench dry-run preview — makes **no** request at
 * all; it is the pure, already existing `buildPreviewRows` of
 * `../../ai-workbench/workbench.js`.
 */
import type { FiAnalyticsSummary } from "../../insights-analytics/api.js";
import type { GiCoverageGapsView } from "../../gi-coverage/api.js";
import type { ProviderRegistry } from "../../tenants/tenantAiCapabilitiesEditValidation.js";

import { gtccGet, type GtccResult } from "./gtcc-result.js";

/* --------------------------------------------------------------- windows */

/** The reference's coverage filter: "Filter 7/30/90 Tage". */
export const AIA_WINDOW_DAYS = [7, 30, 90] as const;
export type AiaWindowDays = (typeof AIA_WINDOW_DAYS)[number];

/** The reference heads Question Analytics with "30 Tage". */
export const AIA_ANALYTICS_DEFAULT_DAYS: AiaWindowDays = 30;

/**
 * Start of a filter window as `YYYY-MM-DD`. This is a *request parameter*,
 * not a displayed measurement — same derivation the existing coverage page
 * uses (`gi-coverage/AdminGiCoveragePage.tsx:102`). No rendered value is ever
 * derived from the local clock.
 */
export function aiaWindowFrom(days: AiaWindowDays, now: number = Date.now()): string {
  return new Date(now - days * 86_400_000).toISOString().slice(0, 10);
}

/* --------------------------------------------------------------- paths */

export const AIA_PROVIDER_REGISTRY_PATH = "/v1/admin/ai/provider-registry";
export const AIA_ANALYTICS_PATH = "/v1/insights/analytics";
export const AIA_COVERAGE_PATH = "/v1/admin/ai/gi-coverage-gaps";
export const AIA_USAGE_PATH = "/v1/admin/ai/usage";

/** How many coverage groups the panel asks for; the reference lists a few. */
export const AIA_COVERAGE_LIMIT = 10;

export function aiaAnalyticsPath(days: AiaWindowDays): string {
  return `${AIA_ANALYTICS_PATH}?days=${days}`;
}

export function aiaCoveragePath(days: AiaWindowDays, now?: number): string {
  const from = now === undefined ? aiaWindowFrom(days) : aiaWindowFrom(days, now);
  return `${AIA_COVERAGE_PATH}?from=${from}&limit=${AIA_COVERAGE_LIMIT}`;
}

/** Grouped by tenant — the panel is titled "AI-Nutzung je Tenant". */
export const AIA_USAGE_QUERY_PATH = `${AIA_USAGE_PATH}?groupBy=tenant`;

/* ---------------------------------------------------------------- types */

/** `{ registry }` envelope of `/v1/admin/ai/provider-registry`. */
export interface AiaProviderRegistryDto {
  readonly registry: ProviderRegistry;
}

/** `{ coverage }` envelope of `/v1/admin/ai/gi-coverage-gaps`. */
export interface AiaCoverageDto {
  readonly coverage: GiCoverageGapsView;
}

/**
 * The counters of `/v1/admin/ai/usage` the panel prints. Mirrors
 * `services/api/src/modules/admin/ai-usage.service.ts:48` without copying
 * the fields the panel does not show; every one stays optional because a
 * partial payload must read "nicht messbar", never zero.
 */
export interface AiaUsageMetricsDto {
  readonly invocations?: number;
  readonly providerCalledCount?: number;
  readonly totalTokens?: number;
}

export interface AiaUsageGroupDto extends AiaUsageMetricsDto {
  readonly tenantId: string | null;
}

export interface AiaUsageViewDto {
  readonly groupBy?: string;
  /** `false` while no provider price is verified — printed as "unverified". */
  readonly costVerified?: boolean;
  readonly groups?: ReadonlyArray<AiaUsageGroupDto>;
  readonly totals?: AiaUsageMetricsDto;
}

/** `{ usage }` envelope of `/v1/admin/ai/usage`. */
export interface AiaUsageDto {
  readonly usage: AiaUsageViewDto;
}

/* -------------------------------------------------------------- fetchers */

export function fetchAiaProviderRegistry(
  signal?: AbortSignal,
): Promise<GtccResult<AiaProviderRegistryDto>> {
  return gtccGet<AiaProviderRegistryDto>(AIA_PROVIDER_REGISTRY_PATH, signal);
}

export function fetchAiaAnalytics(
  days: AiaWindowDays,
  signal?: AbortSignal,
): Promise<GtccResult<FiAnalyticsSummary>> {
  return gtccGet<FiAnalyticsSummary>(aiaAnalyticsPath(days), signal);
}

export function fetchAiaCoverage(
  days: AiaWindowDays,
  signal?: AbortSignal,
): Promise<GtccResult<AiaCoverageDto>> {
  return gtccGet<AiaCoverageDto>(aiaCoveragePath(days), signal);
}

export function fetchAiaUsage(signal?: AbortSignal): Promise<GtccResult<AiaUsageDto>> {
  return gtccGet<AiaUsageDto>(AIA_USAGE_QUERY_PATH, signal);
}

/* ----------------------------------------------------------- query keys */

/*
 * The registry prefix of this view is `["gtcc", "insights-analytics"]` — the
 * segment the predecessor `GtccAiAnalyticsView.tsx` fetched under and the one
 * `GTCC_QUERY_KEY_INVENTORY.md` records for `aia`. Keeping it means the view
 * boundary of the deck keeps cancelling and clearing this view unchanged.
 *
 * Each key is an own exported array literal so the static key-consistency
 * guard can follow `queryKey: AIA_…_KEY` to its literal.
 */
export const AIA_PROVIDER_REGISTRY_KEY = [
  "gtcc",
  "insights-analytics",
  "provider-registry",
] as const;
export const AIA_USAGE_KEY = ["gtcc", "insights-analytics", "ai-usage"] as const;

/** Window-scoped keys: the days segment keeps the two filters apart. */
export function aiaAnalyticsKey(days: AiaWindowDays): readonly [string, string, string, number] {
  return ["gtcc", "insights-analytics", "analytics", days];
}

export function aiaCoverageKey(days: AiaWindowDays): readonly [string, string, string, number] {
  return ["gtcc", "insights-analytics", "coverage", days];
}

export const AIA_QUERY_KEYS = {
  providerRegistry: AIA_PROVIDER_REGISTRY_KEY,
  usage: AIA_USAGE_KEY,
  analytics: aiaAnalyticsKey,
  coverage: aiaCoverageKey,
} as const;

export type { FiAnalyticsSummary, GiCoverageGapsView, GtccResult, ProviderRegistry };
